import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt, FaPlus } from "react-icons/fa";
import Modal from "../../../components/Modal";
import { createSchoolYear } from "../api";
import { upsertCachedSchoolYear } from "../cache";
import { BackButton, LdsPage, StatusBadge } from "../components/Lds";
import type { SchoolYearFormValues } from "../types";
import { httpStatus } from "../utils";
import SchoolYearForm from "./SchoolYearForm";

import { useLeaveForm } from "../../../components/navigation/BackButton";
const SchoolYearNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const [values, setValues] = useState<SchoolYearFormValues>({ startDate: "", endDate: "", defaultMonthlyFee: null, selfSignupDeadline: "" });
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ show: boolean; type: string; title: string; message: string; navigateOnClose?: string }>({ show: false, type: "success", title: "", message: "" });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!values.startDate || !values.endDate) {
      setModal({
        show: true,
        type: "error",
        title: t("common.validation_error", { defaultValue: "Validation error" }),
        message: t("years.edit.validation_dates", { defaultValue: "Please select both start and end dates." }),
      });
      return;
    }
    setSaving(true);
    try {
      const created = await createSchoolYear(values);
      upsertCachedSchoolYear(created);
      setModal({
        show: true,
        type: "success",
        title: t("common.success", { defaultValue: "Success" }),
        message: t("years.new.created_ok", { defaultValue: "School year created successfully." }),
        navigateOnClose: "/school/years",
      });
    } catch (err) {
      setModal({
        show: true,
        type: "error",
        title: t("common.error", { defaultValue: "Error" }),
        message:
          httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("years.new.create_failed", { defaultValue: "Failed to create school year. Please try again." }),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LdsPage>
      <div className="space-y-5">
        <BackButton fallback="/school/years" />
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[30%]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
              <FaPlus aria-hidden="true" />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("years.overview.title", { defaultValue: "School years" })}
              </p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                {t("years.new.title", { defaultValue: "New school year" })}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                {t("years.new.subtitle", {
                  defaultValue: "Create a school year and configure its dates and financial settings.",
                })}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge tone="info">
                  <span className="inline-flex items-center gap-1.5">
                    <FaCalendarAlt aria-hidden="true" />
                    {t("years.details.range", { defaultValue: "School year" })}
                  </span>
                </StatusBadge>
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaCalendarAlt className="text-[110px] text-indigo-200/70 dark:text-indigo-800/40" />
          </div>
        </section>
        <SchoolYearForm
          values={values}
          onChange={setValues}
          onSubmit={handleSubmit}
          onCancel={() => navigate("/school/years")}
          submitting={saving}
          submitLabel={t("years.new.create", { defaultValue: "Create school year" })}
        />
      </div>
      <Modal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          setModal({ show: false, type: "success", title: "", message: "" });
          if (modal.navigateOnClose) leaveForm(modal.navigateOnClose);
        }}
      />
    </LdsPage>
  );
};

export default SchoolYearNew;
